import { Terminator } from "./terminable";
import { Notifier } from "./notifier";
export class VirtualObject {
    #terminator = new Terminator();
    #notifier;
    #data;
    #proxy;
    constructor(data) {
        this.#data = data;
        this.#notifier = this.#terminator.own(new Notifier());
        this.#proxy = this.#createProxy(data, []);
    }
    get data() { return this.#data; }
    get proxy() { return this.#proxy; }
    subscribe(observer, ...path) {
        const getValue = () => path.reduce((object, key) => object[key], this.#proxy);
        return this.#notifier.subscribe(notifiedPath => {
            if (this.#pathsOverlap(notifiedPath, path)) {
                observer(getValue());
            }
        });
    }
    catchupAndSubscribe(observer, ...path) {
        const getValue = () => path.reduce((object, key) => object[key], this.#proxy);
        observer(getValue());
        return this.subscribe(observer, ...path);
    }
    subscribeAll(observer) {
        return this.#notifier.subscribe(path => observer(path[0]));
    }
    createMutableObservableValue(...path) {
        const terminator = this.#terminator.own(new Terminator());
        const notifier = terminator.own(new Notifier());
        const getTarget = () => path.slice(0, -1).reduce((object, key) => object[key], this.#proxy);
        const lastKey = path[path.length - 1];
        const observable = {
            getValue: () => path.reduce((object, key) => object[key], this.#proxy),
            setValue: (value) => getTarget()[lastKey] = value,
            subscribe: (observer) => notifier.subscribe(observer),
            catchupAndSubscribe: (observer) => {
                observer(observable);
                return notifier.subscribe(observer);
            },
            terminate: () => terminator.terminate()
        };
        terminator.own(this.subscribe(() => notifier.notify(observable), ...path));
        return observable;
    }
    update(data) {
        const changedRootKeys = new Set();
        this.#updateRecursive(this.#data, data, changedRootKeys);
        for (const key of changedRootKeys) {
            this.#notifier.notify([key]);
        }
    }
    terminate() {
        this.#terminator.terminate();
        this.#notifier.terminate();
    }
    #pathsOverlap = (a, b) => {
        const minLen = Math.min(a.length, b.length);
        for (let i = 0; i < minLen; i++) {
            if (a[i] !== b[i]) {
                return false;
            }
        }
        return true;
    };
    #updateRecursive = (target, source, changedRootKeys, rootKey) => {
        for (const key of Object.keys(source)) {
            const currentRootKey = rootKey ?? key;
            const newValue = source[key];
            const oldValue = target[key];
            if (newValue !== null && typeof newValue === "object" && !Array.isArray(newValue)) {
                this.#updateRecursive(target[key], newValue, changedRootKeys, currentRootKey);
            }
            else if (oldValue !== newValue) {
                target[key] = newValue;
                changedRootKeys.add(currentRootKey);
            }
        }
    };
    #createProxy = (object, path) => new Proxy(object, {
        get: (target, property) => {
            const value = target[property];
            if (value !== null && typeof value === "object" && !Array.isArray(value)) {
                return this.#createProxy(value, [...path, property]);
            }
            return value;
        },
        set: (target, property, value) => {
            ;
            target[property] = value;
            this.#notifier.notify([...path, property]);
            return true;
        },
        preventExtensions: () => false
    });
}
